import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { fetchAuthSession } from 'aws-amplify/auth';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import config from '../config';
import {
  Container,
  SpaceBetween,
  Box,
  Alert,
  Button,
  Form
} from '@cloudscape-design/components';

const stripePromise = loadStripe(config.StripePublishableKey);

function PaymentForm({ clientSecret, customerId, theme, isTrialUser }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [isTrialLoading, setIsTrialLoading] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [error, setError] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [elementReady, setElementReady] = useState(false);
  const [showExtensionWarning, setShowExtensionWarning] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Detect if PaymentElement fails to become ready (extension interference)
  useEffect(() => {
    if (elementReady) return;
    const timer = setTimeout(() => {
      if (!elementReady) {
        setShowExtensionWarning(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [elementReady]);

  const handleCheckoutFallback = async () => {
    try {
      setCheckoutLoading(true);
      setError('');
      const session = await fetchAuthSession();
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      const command = new InvokeCommand({
        FunctionName: config.StripePaymentFunctionArn,
        Payload: JSON.stringify({
          action: 'create_checkout_session',
          email: session.tokens?.idToken?.payload?.email,
          userPoolSub: session.tokens?.idToken?.payload?.sub,
          successUrl: `${window.location.origin}/payment-setup`,
          cancelUrl: `${window.location.origin}/payment-setup`
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));

      if (result.statusCode === 200) {
        const data = JSON.parse(result.body);
        window.location.href = data.checkoutUrl;
      } else {
        const errorBody = JSON.parse(result.body);
        throw new Error(errorBody.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Checkout fallback error:', err);
      setError(err.message || 'Failed to open secure checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('handleSubmit called');
    console.log('stripe:', !!stripe, 'elements:', !!elements, 'clientSecret:', !!clientSecret);
    
    if (!stripe || !elements || !clientSecret) {
      console.error('Missing required Stripe objects');
      return;
    }

    if (!cardholderName.trim()) {
      setError('Please enter the cardholder name.');
      return;
    }

    setIsLoading(true);
    setIsProcessingPayment(true);
    setError('');

    try {
      console.log('Calling stripe.confirmSetup...');
      
      // Confirm the setup with PaymentElement
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-setup`,
          payment_method_data: {
            billing_details: {
              name: cardholderName
            }
          }
        },
        redirect: 'if_required',
      });

      console.log('stripe.confirmSetup result:', { error: stripeError, setupIntent });

      if (stripeError) {
        console.error('Stripe confirmSetup error:', stripeError);
        
        // Provide user-friendly error messages
        let errorMessage = stripeError.message;
        if (stripeError.type === 'card_error') {
          errorMessage = 'Your card was declined. Please try a different payment method.';
        } else if (stripeError.type === 'validation_error') {
          errorMessage = 'Please check your card details and try again.';
        }
        
        throw new Error(errorMessage);
      }
      
      // Check if 3DS authentication is still pending
      if (setupIntent.status === 'requires_action' || setupIntent.status === 'requires_confirmation') {
        throw new Error('Payment authentication is required. Please complete the verification and try again.');
      }
      
      console.log('Setup intent confirmed successfully:', setupIntent.id);

      // Save payment method to backend
      console.log('Fetching auth session...');
      const session = await fetchAuthSession();
      console.log('Auth session obtained');
      
      const lambda = new LambdaClient({
        region: config.Region,
        credentials: session.credentials
      });

      console.log('Calling Lambda to save payment method...');
      const command = new InvokeCommand({
        FunctionName: config.StripePaymentFunctionArn,
        Payload: JSON.stringify({
          action: 'save_payment_method',
          paymentMethodId: setupIntent.payment_method,
          email: session.tokens?.idToken?.payload?.email,
          customerId: customerId,
          userPoolSub: session.tokens?.idToken?.payload?.sub
        })
      });

      const response = await lambda.send(command);
      const result = JSON.parse(new TextDecoder().decode(response.Payload));
      
      console.log('Lambda response:', result);
      
      if (result.statusCode === 200) {
        console.log('Payment method saved successfully');
        // Force token refresh to get updated claims
        await fetchAuthSession({ forceRefresh: true });
        // Navigate away immediately via full page load to avoid
        // AuthContext re-render flashing a different view.
        const redirectPath = sessionStorage.getItem('recapify_redirect_after_setup') || '/recordings';
        sessionStorage.removeItem('recapify_redirect_after_setup');
        window.location.href = redirectPath;
      } else {
        // Extract error message from Lambda response
        const errorBody = JSON.parse(result.body);
        console.error('Lambda returned error:', errorBody);
        throw new Error(errorBody.error || 'Failed to save payment method');
      }
    } catch (err) {
      console.error('Payment setup error:', err);
      setError(err.message || 'Payment setup failed. Please try again.');
      setIsLoading(false);
      setIsProcessingPayment(false);
    }
  };

  return (
    <div style={{ 
      display: "flex", 
      justifyContent: "center", 
      paddingTop: "40px", 
      minHeight: "100vh", 
      backgroundColor: theme === 'dark' ? '#0f0f1e' : '#f9fafb'
    }}>
      <div style={{ maxWidth: "600px", width: "100%" }}>
        <Container>
          <SpaceBetween size="l">
            <div style={{ textAlign: "center" }}>
              <Box variant="h1" fontSize="heading-xl" fontWeight="bold" padding={{ bottom: "xs" }}>
                Payment Setup
              </Box>
              <Box variant="p" color="text-body-secondary">
                Add a payment method to start using Recapify
              </Box>
            </div>

            <Alert type="info">
              We require a credit card to ensure valid usage, prevent fraud, and cover any costs generated by your account. 
              <br></br>
              <br></br>
              You'll receive trial credits to get started, and you're only billed for what you actually use after those credits are consumed. Usage is billed monthly, and you'll receive an itemized invoice detailing all incurred costs. 
              <br></br>
              <br></br>
              There are no commitments, and you can close your account anytime.
            </Alert>

            {error && (
              <Alert type="error" dismissible onDismiss={() => setError('')}>
                {error}
                {error.includes('authentication') && (
                  <>
                    <br /><br />
                    If you see a popup blocker, please allow popups for this site and try again.
                  </>
                )}
              </Alert>
            )}

            <Form>
              <SpaceBetween size="l">
                {showExtensionWarning && !elementReady && (
                  <Alert type="warning" header="Payment form not loading?">
                    A browser extension may be interfering with the secure payment form. 
                    You can try disabling password managers or autofill extensions, or use our secure checkout page instead.
                    <br /><br />
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        variant="primary"
                        loading={checkoutLoading}
                        onClick={handleCheckoutFallback}
                      >
                        Use Secure Checkout Instead
                      </Button>
                    </div>
                  </Alert>
                )}
                <Box>
                  {elementReady && (
                  <div style={{ marginBottom: '4px' }}>
                    <label style={{
                      color: theme === 'dark' ? '#e0e0e0' : '#374151',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'block',
                      marginBottom: '6px'
                    }}>Cardholder name</label>
                    <input
                      type="text"
                      value={cardholderName}
                      onChange={(e) => setCardholderName(e.target.value)}
                      placeholder="Full name on card"
                      aria-label="Cardholder name"
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: theme === 'dark' ? '#1e1e3f' : '#ffffff',
                        border: theme === 'dark' ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: '8px',
                        color: theme === 'dark' ? '#e0e0e0' : '#1f2937',
                        fontSize: '16px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        boxSizing: 'border-box',
                        outline: 'none',
                      }}
                      onFocus={(e) => {
                        e.target.style.border = '1px solid #667eea';
                        e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.border = theme === 'dark' ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(59, 130, 246, 0.2)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  )}
                  <PaymentElement options={{ fields: { billingDetails: { name: 'never' } } }} onReady={() => setElementReady(true)} />
                </Box>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Box variant="small" color="text-body-secondary">
                    🔒 Secure Stripe payments • Pay per use • Monthly invoices • Cancel anytime
                  </Box>
                </div>
                {isProcessingPayment && (
                  <Alert type="info">
                    Processing your payment method... If your bank requires additional verification, a popup or redirect may appear.
                  </Alert>
                )}
                <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                  <Button
                    variant="primary"
                    loading={isLoading}
                    onClick={handleSubmit}
                    disabled={!stripe || !clientSecret || isTrialLoading}
                  >
                    {isTrialUser ? 'Add Payment Method' : 'Complete Setup'}
                  </Button>
                  {!isTrialUser && (
                    <Button
                      variant="normal"
                      loading={isTrialLoading}
                      onClick={async () => {
                        try {
                          setIsTrialLoading(true);
                          const session = await fetchAuthSession();
                          const lambda = new LambdaClient({
                            region: config.Region,
                            credentials: session.credentials
                          });

                          const command = new InvokeCommand({
                            FunctionName: config.StripePaymentFunctionArn,
                            Payload: JSON.stringify({
                              action: 'start_trial',
                              email: session.tokens?.idToken?.payload?.email,
                              userPoolSub: session.tokens?.idToken?.payload?.sub
                            })
                          });

                          const response = await lambda.send(command);
                          const result = JSON.parse(new TextDecoder().decode(response.Payload));
                          
                          if (result.statusCode === 200) {
                            // Force token refresh to get updated claims with trialPhase
                            await fetchAuthSession({ forceRefresh: true });
                            // Navigate away immediately via full page load to avoid
                            // AuthContext re-render flashing the payment-only view.
                            // The destination page will call checkAuthState() on mount.
                            const redirectPath = sessionStorage.getItem('recapify_redirect_after_setup') || '/recordings';
                            sessionStorage.removeItem('recapify_redirect_after_setup');
                            window.location.href = redirectPath;
                          } else {
                            const errorBody = JSON.parse(result.body);
                            setError(errorBody.error || 'Failed to start trial');
                          }
                        } catch (err) {
                          console.error('Trial setup error:', err);
                          setError(err.message || 'Failed to start trial');
                        } finally {
                          setIsTrialLoading(false);
                        }
                      }}
                      disabled={isLoading}
                    >
                      Explore First
                    </Button>
                  )}
                </div>
              </SpaceBetween>
            </Form>
          </SpaceBetween>
        </Container>
      </div>
    </div>
  );
}

function PaymentSetup({ onPaymentSetup }) {
  const [clientSecret, setClientSecret] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [theme, setTheme] = useState(() => localStorage.getItem('cloudscape-theme') || 'light');
  const [isTrialUser, setIsTrialUser] = useState(false);
  const [loadingError, setLoadingError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Listen for theme changes
  useEffect(() => {
    const handleStorageChange = () => {
      setTheme(localStorage.getItem('cloudscape-theme') || 'light');
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case theme changes in same tab
    const interval = setInterval(() => {
      const currentTheme = localStorage.getItem('cloudscape-theme') || 'light';
      if (currentTheme !== theme) {
        setTheme(currentTheme);
      }
    }, 100);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [theme]);

  // We need clientSecret before rendering Elements
  useEffect(() => {
    const initSetup = async () => {
      try {
        setIsLoading(true);
        setLoadingError('');
        
        const session = await fetchAuthSession();
        
        // Check if user is already in trial mode
        const trialPhase = session.tokens?.idToken?.payload?.trialPhase;
        if (trialPhase === 'true') {
          setIsTrialUser(true);
        }
        
        // Check if returning from 3DS redirect
        const urlParams = new URLSearchParams(window.location.search);
        const setupIntentClientSecret = urlParams.get('setup_intent_client_secret');
        const checkoutSessionId = urlParams.get('checkout_session_id');
        
        if (checkoutSessionId) {
          console.log('Returning from Stripe Checkout, processing session...');
          
          const lambda = new LambdaClient({
            region: config.Region,
            credentials: session.credentials
          });

          const command = new InvokeCommand({
            FunctionName: config.StripePaymentFunctionArn,
            Payload: JSON.stringify({
              action: 'handle_checkout_success',
              sessionId: checkoutSessionId
            })
          });

          const response = await lambda.send(command);
          const result = JSON.parse(new TextDecoder().decode(response.Payload));
          
          if (result.statusCode === 200) {
            console.log('Checkout session processed successfully');
            window.history.replaceState({}, document.title, '/payment-setup');
            await fetchAuthSession({ forceRefresh: true });
            const redirectPath = await onPaymentSetup(true);
            if (redirectPath) {
              // Will be handled by navigate in the calling context
              window.location.href = redirectPath;
            }
            return;
          } else {
            const errorBody = JSON.parse(result.body);
            setLoadingError(errorBody.error || 'Failed to process checkout. Please try again.');
            window.history.replaceState({}, document.title, '/payment-setup');
            setIsLoading(false);
            return;
          }
        }
        
        if (setupIntentClientSecret) {
          console.log('Returning from 3DS redirect, retrieving setup intent...');
          
          // Retrieve the SetupIntent to check its status
          const stripeInstance = await stripePromise;
          
          if (!stripeInstance) {
            throw new Error('Failed to load Stripe. Please check your internet connection and disable any ad blockers.');
          }
          
          const { setupIntent, error } = await stripeInstance.retrieveSetupIntent(setupIntentClientSecret);
          
          if (error) {
            console.error('Error retrieving setup intent:', error);
            setLoadingError(error.message);
            setIsLoading(false);
            return;
          }
          
          console.log('Setup intent status after redirect:', setupIntent.status);
          
          if (setupIntent.status === 'succeeded') {
            // Payment method was successfully set up, save it
            console.log('3DS authentication succeeded, saving payment method...');
            
            const lambda = new LambdaClient({
              region: config.Region,
              credentials: session.credentials
            });

            const command = new InvokeCommand({
              FunctionName: config.StripePaymentFunctionArn,
              Payload: JSON.stringify({
                action: 'save_payment_method',
                paymentMethodId: setupIntent.payment_method,
                email: session.tokens?.idToken?.payload?.email,
                customerId: null,
                userPoolSub: session.tokens?.idToken?.payload?.sub
              })
            });

            const response = await lambda.send(command);
            const result = JSON.parse(new TextDecoder().decode(response.Payload));
            
            if (result.statusCode === 200) {
              console.log('Payment method saved after 3DS redirect');
              // Clean URL and trigger success
              window.history.replaceState({}, document.title, '/payment-setup');
              await fetchAuthSession({ forceRefresh: true });
              const redirectPath = await onPaymentSetup(true);
              if (redirectPath) {
                window.location.href = redirectPath;
              }
              return;
            }
          }
        }
        
        // Normal flow: create new setup intent
        const lambda = new LambdaClient({
          region: config.Region,
          credentials: session.credentials
        });

        const command = new InvokeCommand({
          FunctionName: config.StripePaymentFunctionArn,
          Payload: JSON.stringify({ action: 'create_setup_intent' })
        });

        console.log('Calling Lambda to create setup intent...');
        const response = await lambda.send(command);
        const result = JSON.parse(new TextDecoder().decode(response.Payload));
        
        console.log('Lambda response:', result);
        
        if (result.statusCode === 200) {
          const data = JSON.parse(result.body);
          setClientSecret(data.clientSecret);
          setCustomerId(data.customerId);
          setIsLoading(false);
        } else {
          const errorBody = JSON.parse(result.body);
          throw new Error(errorBody.error || 'Failed to initialize payment setup');
        }
      } catch (err) {
        console.error('Error initializing payment setup:', err);
        setLoadingError(err.message || 'Failed to load payment setup. Please refresh the page and try again.');
        setIsLoading(false);
      }
    };
    
    initSetup();
  }, [onPaymentSetup]);

  if (loadingError) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: "100vh",
        backgroundColor: theme === 'dark' ? '#0f0f1e' : '#f9fafb',
        padding: '20px'
      }}>
        <Container>
          <SpaceBetween size="l">
            <Alert type="error" header="Payment Setup Error">
              {loadingError}
              <br /><br />
              <strong>Troubleshooting tips:</strong>
              <ul>
                <li>Check your internet connection</li>
                <li>Disable ad blockers or browser extensions</li>
                <li>Try a different browser</li>
                <li>Refresh the page</li>
              </ul>
            </Alert>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </SpaceBetween>
        </Container>
      </div>
    );
  }

  if (!clientSecret || isLoading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: "100vh",
        backgroundColor: theme === 'dark' ? '#0f0f1e' : '#f9fafb'
      }}>
        <Box variant="p">Loading payment setup...</Box>
      </div>
    );
  }

  // Theme-aware Stripe appearance
  const stripeAppearance = {
    theme: 'none',
    variables: {
      colorPrimary: '#667eea',
      colorBackground: theme === 'dark' ? '#1a1a2e' : '#ffffff',
      colorText: theme === 'dark' ? '#e0e0e0' : '#1f2937',
      colorDanger: '#dc2626',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
    rules: {
      '.Input': {
        backgroundColor: theme === 'dark' ? '#1e1e3f' : '#ffffff',
        border: theme === 'dark' ? '1px solid rgba(102, 126, 234, 0.3)' : '1px solid rgba(59, 130, 246, 0.2)',
        boxShadow: 'none',
        padding: '12px',
        color: theme === 'dark' ? '#e0e0e0' : '#1f2937',
      },
      '.Input:focus': {
        border: '1px solid #667eea',
        boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
      },
      '.Label': {
        color: theme === 'dark' ? '#e0e0e0' : '#374151',
        fontSize: '14px',
        fontWeight: '500',
        marginBottom: '6px',
      },
      '.Error': {
        color: '#dc2626',
      },
    }
  };

  return (
    <Elements 
      stripe={stripePromise} 
      options={{ 
        clientSecret,
        appearance: stripeAppearance
      }}
    >
      <PaymentForm 
        clientSecret={clientSecret} 
        customerId={customerId} 
        theme={theme}
        isTrialUser={isTrialUser}
      />
    </Elements>
  );
}

export default PaymentSetup;
